import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Row,
  Table,
  Badge,
  Modal,
} from "react-bootstrap";
import { BsPencil, BsTrash, BsEye } from "react-icons/bs";
import { connect } from "react-redux";
import { getTerminal } from "../../actions/Terminal.action";
import { deleteTerminal } from "../../actions/Terminal.action";
import { years } from "../../constants/MonthsAndYears";
import { termArray } from "../../constants/termArray";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const TerminalList = ({ grades, getTerminal, deleteTerminal }) => {
  const navigate = useNavigate();
  const [grade, setGrade] = useState("");
  const [batch, setBatch] = useState("");
  const [shift, setShift] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [term, setTerm] = useState("1");
  const [terminalSubjectId, setTerminalSubjectId] = useState("");
  const [terminals, setTerminals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTerminal, setSelectedTerminal] = useState(null);

  const searchTerminals = async () => {
    if (!grade || !batch || !shift || !year || !term) {
      toast.error("Please select all required fields!");
      return;
    }

    setIsLoading(true);
    try {
      const data = await getTerminal({
        batchId: parseInt(batch),
        year: parseInt(year),
        gradeId: parseInt(grade),
        shiftId: parseInt(shift),
        terminalSubjectId: terminalSubjectId
          ? parseInt(terminalSubjectId)
          : undefined,
        term: parseInt(term),
      });

      if (data && data.length > 0) {
        setTerminals(data);
      } else {
        setTerminals([]);
        toast.info("No terminal entries found");
      }
    } catch (error) {
      toast.error("Error fetching terminal entries");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (terminal) => {
    // Navigate to edit page
    navigate("/admin/edit-terminal");
  };

  const handleDelete = (terminal) => {
    setSelectedTerminal(terminal);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (selectedTerminal) {
      try {
        const success = await deleteTerminal(selectedTerminal.id);
        if (success) {
          // Refresh the list
          searchTerminals();
        }
      } catch (error) {
        toast.error("Error deleting terminal entry");
      }
    }
    setShowDeleteModal(false);
    setSelectedTerminal(null);
  };

  const selectedGrade = grades?.find((g) => g.id === parseInt(grade));
  const terminalSubjects = selectedGrade?.terminalSubjects || [];

  // Group terminals by student for better display
  const groupedTerminals = terminals.reduce((acc, terminal) => {
    const key = `${terminal.studentId}-${
      terminal.Student?.name || `Student ${terminal.studentId}`
    }`;
    if (!acc[key]) {
      acc[key] = {
        studentId: terminal.studentId,
        studentName: terminal.Student?.name || `Student ${terminal.studentId}`,
        terminals: [],
      };
    }
    acc[key].terminals.push(terminal);
    return acc;
  }, {});

  return (
    <div>
      <Card bg="white" text="dark" className={`crd shadow`}>
        <Card.Body>
          <h4 className="mb-3">Search Terminal Entries</h4>
          <Row>
            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="grade" className="d-block">
                  Class
                </label>
              </div>
              <Form.Select
                onChange={(e) => {
                  setBatch("");
                  setShift("");
                  setGrade(e.target.value);
                  setTerminalSubjectId("");
                }}
              >
                <option value={""}>Select Class</option>
                {grades &&
                  grades.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
              </Form.Select>
            </Col>
            {grade !== "" ? (
              <Col md={3} className="py-3">
                <div className="d-flex justify-content-between align-items-center pb-2">
                  <label htmlFor="shift" className="d-block">
                    Shift
                  </label>
                </div>
                <Form.Select
                  onChange={(e) => {
                    setBatch("");
                    setShift(e.target.value);
                  }}
                >
                  <option value={""}>Select Shift</option>
                  {grades
                    .filter((grd) => grd.id === parseInt(grade))[0]
                    .shifts.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                </Form.Select>
              </Col>
            ) : (
              <></>
            )}
            {grade !== "" && shift !== "" ? (
              <Col md={3} className="py-3">
                <div className="d-flex justify-content-between align-items-center pb-2">
                  <label htmlFor="batch" className="d-block">
                    Batch
                  </label>
                </div>
                <Form.Select onChange={(e) => setBatch(e.target.value)}>
                  <option value={""}>Select Batch</option>
                  {grades
                    .filter((grd) => grd.id === parseInt(grade))[0]
                    .batches.filter((bt) => bt.shiftId === parseInt(shift))
                    .map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                </Form.Select>
              </Col>
            ) : (
              <></>
            )}
            {grade !== "" && shift !== "" && batch !== "" ? (
              <Col
                md={3}
                className="d-flex justify-content-end align-items-end py-3"
              >
                <Button
                  onClick={searchTerminals}
                  variant="primary"
                  type="button"
                  className="btn_primary"
                  disabled={isLoading}
                >
                  {isLoading ? "Searching..." : "Search"}
                </Button>
              </Col>
            ) : (
              <></>
            )}
          </Row>
          <Row>
            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="terminalSubject" className="d-block">
                  Terminal Subject (Optional)
                </label>
              </div>
              <Form.Select
                onChange={(e) => setTerminalSubjectId(e.target.value)}
                value={terminalSubjectId}
              >
                <option value={""}>All Subjects</option>
                {terminalSubjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="term" className="d-block">
                  Term
                </label>
              </div>
              <Form.Select
                onChange={(e) => setTerm(e.target.value)}
                value={term}
              >
                {termArray.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="year" className="d-block">
                  Year
                </label>
              </div>
              <Form.Select
                value={year}
                onChange={(e) => setYear(e.target.value)}
              >
                {years.map((item, i) => (
                  <option key={i} value={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {terminals.length > 0 && (
        <Card bg="white" text="dark" className={`crd shadow mt-3`}>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 className="text-center my-auto">
                Terminal Entries ({terminals.length})
              </h3>
            </div>
            <Table responsive striped hover>
              <thead>
                <tr>
                  <th>Student UID</th>
                  <th>Student Name</th>
                  <th>Subject</th>
                  <th>Oral Mark</th>
                  <th>Writing Mark</th>
                  <th>Total Mark</th>
                  <th>Percentage</th>
                  <th>Highest Mark</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {terminals.map((terminal) => (
                  <tr key={terminal.id}>
                    <td>{terminal.Student?.uid || terminal.studentId}</td>
                    <td>
                      {terminal.Student?.name ||
                        `Student ${terminal.studentId}`}
                    </td>
                    <td>
                      <Badge color="blue" variant="light">
                        {terminal.TerminalSubject?.name || "N/A"}
                      </Badge>
                    </td>
                    <td>
                      <Badge color="green" variant="light">
                        {parseFloat(terminal.oralMark).toFixed(2)}
                      </Badge>
                    </td>
                    <td>
                      <Badge color="orange" variant="light">
                        {parseFloat(terminal.writingMark).toFixed(2)}
                      </Badge>
                    </td>
                    <td>
                      <span className="fw-bold text-primary">
                        {parseFloat(terminal.totalMark).toFixed(2)}
                      </span>
                    </td>
                    <td>
                      <span className="fw-bold text-success">
                        {parseFloat(terminal.percentage).toFixed(2)}%
                      </span>
                    </td>
                    <td>
                      <Badge color="purple" variant="light">
                        {terminal.highestMark}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleEdit(terminal)}
                          title="Edit"
                        >
                          <BsPencil />
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(terminal)}
                          title="Delete"
                        >
                          <BsTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {terminals.length === 0 && !isLoading && (
        <Card bg="white" text="dark" className={`crd shadow mt-3`}>
          <Card.Body className="text-center py-5">
            <h5 className="text-muted">No terminal entries found</h5>
            <p className="text-muted">
              Try adjusting your search criteria or create new terminal entries.
            </p>
          </Card.Body>
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this terminal entry? This action
          cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const mapStateToProps = (state) => ({
  grades: state.grade.grade,
});

const mapDispatchToProps = {
  getTerminal,
  deleteTerminal,
};

export default connect(mapStateToProps, mapDispatchToProps)(TerminalList);
